import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs, getSubjectWiseGradingSummary } from "../../api/examApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");

/**
 * `exam-report-subject-wise-grading-summary` ("Subject Wise Grade") — found
 * missing while sweeping the app for real, previously-uncovered gaps (see
 * MIGRATION.md). Direct port of digicampusbd's `ExamReportService
 * .classWiseSubjectGradingSummary` — for one section+group, one row per
 * real configured subject (plus a synthetic row for any real "Choosable"
 * 4th-subject, scoped to just the students who took it): per-letter-grade
 * counts, real pass/fail ratios, and the subject's assigned teacher.
 *
 * Deliberate, disclosed simplification: the real endpoint's `typeOfReport=1`
 * variant (a bare class spanning every section at once) isn't built — every
 * other report sharing this shape in this app is already section-by-section,
 * matching this page's own section+group picker.
 */
export default function ExamSubjectWiseGradePage() {
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    groupApi.list().then((list) => {
      setGroups(list);
      if (list.length > 0) setGroupId(String(list[0].id));
    });
    academicYearApi.list().then((list) => {
      setAcademicYears(list);
      if (list.length > 0) setAcademicYear(list[0].name);
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classConfigId, classConfigs]);

  const handleSearch = async () => {
    setError("");
    setRows(null);
    if (!classConfigId || !groupId || !examConfigId || !academicYear) {
      setError("Please select section, group, exam, and academic year.");
      return;
    }
    setLoading(true);
    try {
      const data = await getSubjectWiseGradingSummary(examConfigId, academicYear, classConfigId, groupId);
      setRows(data);
      if (data.length === 0) setError("No Student Found");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to load grading summary.");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { key: "subjectName", label: "Subject" },
    { key: "subjectTeacher", label: "Teacher" },
    { key: "numOfAPlus", label: "A+" },
    { key: "numOfA", label: "A" },
    { key: "numOfAMinus", label: "A-" },
    { key: "numOfB", label: "B" },
    { key: "numOfC", label: "C" },
    { key: "numOfD", label: "D" },
    { key: "numOfF", label: "F" },
    { key: "numOfAbsence", label: "Absent" },
    { key: "subjectPassRatio", label: "Subject Pass %" },
    { key: "subjectFailRatio", label: "Subject Fail %" },
    { key: "totalStudents", label: "Total Students" },
    { key: "totalPass", label: "Total Pass" },
    { key: "totalFail", label: "Total Fail" },
    { key: "totalPassRatio", label: "Overall Pass %" },
    { key: "totalFailRatio", label: "Overall Fail %" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Subject Wise Grade</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>

      <div className="attendance-filters">
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Group
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>

      {rows && rows.length > 0 && <DataTable columns={columns} rows={rows} />}
    </MainLayout>
  );
}
